import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import StatusData from "../../data/StatusData";
import PartyData from "../../data/PartyData";

const VoterInfo = forwardRef(
  ({ onDistrictData, selectedFilters, setSelectedFilters }, ref) => {
    const [optionData, setOptionData] = useState([]);
    const [advancedFilters] = useState([
      "Begins with",
      "Equals",
      "Contains",
      "Ends With",
    ]);
    const [selectedIDNumberFilter, setSelectedIDNumberFilter] = useState("");
    const [selectedHouseWithFilter, setSelectedHouseWithFilter] = useState("");

    useEffect(() => {
      fetchOptionData();
    }, []);

    const fetchOptionData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/data`
        );

        if (res.data) {
          setOptionData(res.data);
        } else {
          console.log("Data is empty or undefined.");
        }
      } catch (error) {
        console.log(error);
      }
    };

    const handleChange = (filterName, newValue, filterType) => {
      if (
        filterName !== "selectedIDNumber" && filterName !== "selectedHouseWith" &&
        filterName !== "selectedIDNumberFilter" && filterName !== "selectedHouseWithFilter"
      ) {
        setSelectedFilters((prevFilters) => ({
          ...prevFilters,
          [filterName]: newValue,
        }));
      } else if (filterName === "selectedGender") {
        setSelectedFilters((prevFilters) => ({
          ...prevFilters,
          [filterName]: newValue ? newValue : ["Male", "Female"],
        }));
      } else if (filterName === "selectedIDNumberFilter") {
        setSelectedIDNumberFilter(newValue);
        const filterTypeValue = getFilterTypeValue(newValue);
        setSelectedFilters((prevFilters) => ({
          ...prevFilters,
          selectedIDNumber: {
            value: prevFilters.selectedIDNumber?.value || "",
            label: prevFilters.selectedIDNumber?.value || "",
            filterType: filterTypeValue,
          },
        }));
      } else if (filterName === "selectedHouseWithFilter") {
        setSelectedHouseWithFilter(newValue);
        const filterTypeValue = getFilterTypeValue(newValue);
        setSelectedFilters((prevFilters) => ({
          ...prevFilters,
          selectedHouseWith: {
            value: prevFilters.selectedHouseWith?.value || "",
            label: prevFilters.selectedHouseWith?.value || "",
            filterType: filterTypeValue,
          },
        }));
      } else {
        setSelectedFilters((prevFilters) => ({
          ...prevFilters,
          [filterName]: {
            value: newValue,
            label: newValue,
            filterType: filterType || "startsWith",
          },
        }));
      }
    };

    const getFilterTypeValue = (label) => {
      switch (label) {
        case "Begins with":
          return "startsWith";
        case "Equals":
          return "equals";
        case "Contains":
          return "contains";
        case "Ends With":
          return "endsWith";
        default:
          return "startsWith";
      }
    };

    const getUniqueOptions = (filter) => {
      switch (filter) {
        case "Voter ID":
          return [...new Set(optionData.IDNumber)];
        case "Party":
          return [...new Set(PartyData.map((item) => item.party))];
        case "Gender":
          return [...new Set(optionData.Gender)];
        case "House With":
          return [...new Set(optionData.HouseNumber)];
        case "Support Status":
          return [...new Set(StatusData.map((item) => item.status))];
        default:
          return [];
      }
    };

    const filterNames4 = [
      "Voter ID",
      "Party",
      "Gender",
      "House With",
      "Support Status",
    ];

    useImperativeHandle(ref, () => ({
      handleRefreshFilter() {
        setSelectedIDNumberFilter("");
        setSelectedHouseWithFilter("");
      },
    }));

    return (
      <div>
        <Card elevation={3} className="m-2 sm:m-4">
          <CardContent>
            <Typography variant="h6" className="mb-3 pt-3 pb-3">
              Identity Search
            </Typography>
            <Grid container spacing={2}>
              {/* Row 1: Begin with + Search for Voter ID */}
              <Grid item xs={6} md={6}>
                <FormControl variant="outlined" fullWidth>
                  <Autocomplete
                    options={advancedFilters}
                    value={selectedIDNumberFilter}
                    onChange={(_, newValue) =>
                      handleChange("selectedIDNumberFilter", newValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Voter ID"
                        variant="outlined"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6} md={6}>
                <FormControl variant="outlined" fullWidth>
                  <TextField
                    label="Search for Voter ID"
                    fullWidth
                    variant="outlined"
                    value={selectedFilters.selectedIDNumber?.value || ""}
                    onChange={(e) =>
                      handleChange(
                        "selectedIDNumber",
                        e.target.value,
                        selectedFilters.selectedIDNumber?.filterType
                      )
                    }
                  />
                </FormControl>
              </Grid>

              {/* Row 2: Select House + Select House */}
              <Grid item xs={6} md={6}>
                <FormControl variant="outlined" fullWidth>
                  <Tooltip 
                    title={(!selectedFilters.selectedParty || selectedFilters.selectedParty.length === 0) ? 'please select "Party" first' : ""}
                    arrow
                  >
                    <span>
                      <Autocomplete
                        options={advancedFilters}
                        value={selectedHouseWithFilter}
                        onChange={(_, newValue) =>
                          handleChange("selectedHouseWithFilter", newValue)
                        }
                        disabled={!selectedFilters.selectedParty || selectedFilters.selectedParty.length === 0}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="House With"
                            variant="outlined"
                          />
                        )}
                      />
                    </span>
                  </Tooltip>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={6}>
                <FormControl variant="outlined" fullWidth>
                  <Tooltip 
                    title={(!selectedFilters.selectedParty || selectedFilters.selectedParty.length === 0) ? 'please select "Party" first' : ""}
                    arrow
                  >
                    <span>
                      <TextField
                        label="House With"
                        fullWidth
                        variant="outlined"
                        value={selectedFilters.selectedHouseWith?.value || ""}
                        onChange={(e) =>
                          handleChange(
                            "selectedHouseWith",
                            e.target.value,
                            selectedFilters.selectedHouseWith?.filterType
                          )
                        }
                        disabled={!selectedFilters.selectedParty || selectedFilters.selectedParty.length === 0}
                      />
                    </span>
                  </Tooltip>
                </FormControl>
              </Grid>

              {/* Row 3: Party + Support Status */}
              <Grid item xs={6} md={6}>
                {filterNames4
                  .filter((filter) => filter === "Party")
                  .map((filter, index) => (
                    <FormControl key={index} variant="outlined" fullWidth>
                      <Autocomplete
                        options={getUniqueOptions(filter)}
                        value={
                          selectedFilters[
                            `selected${filter.split(" ").join("")}`
                          ]
                        }
                        onChange={(_, newValue) =>
                          handleChange(
                            `selected${filter.split(" ").join("")}`,
                            newValue
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter}
                            variant="outlined"
                          />
                        )}
                        multiple={
                          filter === "Party" ||
                          filter === "Gender" ||
                          filter === "House With" ||
                          filter === "Support Status"
                        }
                      />
                    </FormControl>
                  ))}
              </Grid>
              <Grid item xs={6} md={6}>
                {filterNames4
                  .filter((filter) => filter === "Support Status")
                  .map((filter, index) => (
                    <FormControl key={index} variant="outlined" fullWidth sx={{ mb: 2 }}>
                      <Autocomplete
                        options={getUniqueOptions(filter)}
                        value={
                          selectedFilters[
                            `selected${filter.split(" ").join("")}`
                          ]
                        }
                        onChange={(_, newValue) =>
                          handleChange(
                            `selected${filter.split(" ").join("")}`,
                            newValue
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter}
                            variant="outlined"
                          />
                        )}
                        multiple={
                          filter === "Party" ||
                          filter === "Gender" ||
                          filter === "House With" ||
                          filter === "Support Status"
                        }
                      />
                    </FormControl>
                  ))}
              </Grid>

              {/* Full Width Gender Field */}
              <Grid item xs={12}>
                {filterNames4
                  .filter((filter) => filter === "Gender")
                  .map((filter, index) => (
                    <FormControl key={index} variant="outlined" fullWidth>
                      <Autocomplete
                        options={getUniqueOptions(filter)}
                        value={
                          selectedFilters[
                            `selected${filter.split(" ").join("")}`
                          ]
                        }
                        onChange={(_, newValue) =>
                          handleChange(
                            `selected${filter.split(" ").join("")}`,
                            newValue
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter}
                            variant="outlined"
                          />
                        )}
                        multiple={
                          filter === "Party" ||
                          filter === "Gender" ||
                          filter === "House With" ||
                          filter === "Support Status"
                        }
                      />
                    </FormControl>
                  ))}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </div>
    );
  }
);

export default VoterInfo;
